import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import GroupIcon from "@mui/icons-material/Group";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DescriptionIcon from "@mui/icons-material/Description";
import AssessmentIcon from "@mui/icons-material/Assessment";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ScoreIcon from "@mui/icons-material/Score";
import EditIcon from "@mui/icons-material/Edit";
import ThresholdUpdateModal from './ThresholdUpdateModal';

interface JobMatrix {
    total_candidates: number;
    candidates_added_to_talent_pool: number;
    candidates_missing_resume: number;
    evaluated_candidates: number;
    candidates_below_threshold: number;
    score_threshold: number;
}

interface JobMatrixCardsProps {
    jobMatrix: JobMatrix | null;
    expertId: string;
    jobId: string;
    onUpdate?: () => void;
}

const JobMatrixCards: React.FC<JobMatrixCardsProps> = ({ jobMatrix, expertId, jobId, onUpdate }) => {
    const [showModal, setShowModal] = useState(false);

    const statusIconMap: Record<string, JSX.Element> = {
        "Total Candidates": <GroupIcon sx={{ color: "#325CD6" }} />,
        "Evaluated Candidates": <AssessmentIcon sx={{ color: "#325CD6" }} />,
        "Added to Pool": <CheckCircleOutlineIcon sx={{ color: "#325CD6" }} />,
        "Missing Resume": <DescriptionIcon sx={{ color: "#325CD6" }} />,
        "Below Threshold": <TrendingDownIcon sx={{ color: "#325CD6" }} />,
        "Score Threshold (%)": <ScoreIcon sx={{ color: "#325CD6" }} />,
    };

    const jobMatrixMap = [
        { label: "Total Candidates", key: "total_candidates" },
        { label: "Evaluated Candidates", key: "evaluated_candidates" },
        { label: "Added to Pool", key: "candidates_added_to_talent_pool" },
        { label: "Missing Resume", key: "candidates_missing_resume" },
        { label: "Below Threshold", key: "candidates_below_threshold" },
        { label: "Score Threshold (%)", key: "score_threshold", editable: true },
    ];

    const handleEditClick = () => {
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleUpdate = () => {
        if (onUpdate) {
            onUpdate();
        }
    };

    return (
        <Box sx={{ display: "flex", gap: 4, px: 2, py: 3 }}>
            {jobMatrixMap.map(({ label, key, editable }, index) => (
                <Box
                    key={index}
                    sx={{
                        width: "15%",
                        minWidth: 140,
                        textAlign: "left",
                        border: "1px solid #E5EAF2",
                        borderRadius: "12px",
                        py: 2,
                        px: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "left",
                        justifyContent: "left",
                        position: "relative",
                    }}
                >
                    <Box
                        sx={{
                            bgcolor: "#325CD61A",
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 1.5,
                        }}
                    >
                        {statusIconMap[label]}
                    </Box>
                    <Typography
                        variant="body2"
                        fontWeight={500}
                        color="#1A1A1A"
                    >
                        {label}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography
                            variant="h6"
                            fontWeight={700}
                            color="#1A1A1A"
                        >
                            {jobMatrix
                                ? jobMatrix[key as keyof typeof jobMatrix] ?? 0
                                : 0}
                        </Typography>
                        {editable && (
                            <IconButton
                                size="small"
                                onClick={handleEditClick}
                                sx={{
                                    color: '#5175e0',
                                    '&:hover': {
                                        bgcolor: 'rgba(81, 117, 224, 0.08)',
                                    },
                                }}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                </Box>
            ))}

            {/* Threshold Update Modal */}
            {jobMatrix && (
                <ThresholdUpdateModal
                    open={showModal}
                    onClose={handleCloseModal}
                    currentThreshold={jobMatrix.score_threshold}
                    expertId={expertId}
                    jobId={jobId}
                    onUpdate={handleUpdate}
                />
            )}
        </Box>
    );
};

export default JobMatrixCards;


